import type { TopCustomer } from "@/apis/gql/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Building2 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";

interface TopSuppliersChartProps {
  data: TopCustomer[];
  currency?: string;
}

const chartConfig = {
  totalAmount: {
    label: "Transaktionsvolumen",
    color: "hsl(var(--chart-5))",
  },
} satisfies ChartConfig;

export function TopSuppliersChart({
  data,
  currency = "EUR",
}: TopSuppliersChartProps) {
  const chartData = data.map((supplier) => ({
    ...supplier,
    totalAmount: supplier.totalAmount,
  }));

  // Use a gradient of orange colors
  const getBarColor = (index: number) => {
    const colors = [
      "hsl(24, 95%, 60%)", // Bright orange
      "hsl(24, 90%, 55%)",
      "hsl(24, 85%, 50%)",
      "hsl(24, 80%, 48%)",
      "hsl(24, 75%, 45%)",
      "hsl(24, 70%, 43%)",
      "hsl(24, 65%, 40%)",
      "hsl(24, 60%, 38%)",
      "hsl(24, 55%, 35%)",
      "hsl(24, 50%, 32%)", // Darker orange
    ];
    return colors[index % colors.length];
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div>
            <CardTitle className="text-xl font-semibold">
              Top 10 Lieferanten
            </CardTitle>
            <CardDescription className="mt-0.5">
              Nach Umsatz sortiert
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[400px] text-muted-foreground">
            Keine Daten für den ausgewählten Zeitraum verfügbar
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[400px] w-full">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ left: 20, right: 20 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                className="stroke-muted/30"
              />
              <XAxis
                type="number"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) =>
                  new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency,
                    notation: "compact",
                    compactDisplay: "short",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(value)
                }
                className="text-sm"
                tick={{ fill: "hsl(var(--muted-foreground))" }}
              />
              <YAxis
                type="category"
                dataKey="entityName"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={200}
                className="text-xs"
                tick={{ fill: "hsl(var(--foreground))" }}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    indicator="line"
                    labelFormatter={(label) => label}
                    formatter={(value, name) => {
                      const numValue = Number(value);
                      return [
                        new Intl.NumberFormat("de-DE", {
                          style: "currency",
                          currency,
                        }).format(numValue),
                        chartConfig.totalAmount.label,
                      ];
                    }}
                  />
                }
              />
              <Bar dataKey="totalAmount" radius={[0, 6, 6, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={entry.entityId || `cell-${index}`}
                    fill={getBarColor(index)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
